import { notifications } from '@mantine/notifications';
import { CheckCircle, XCircle, WarningCircle, Info } from '@phosphor-icons/react';

export interface ToastOptions {
  id?: string;
  autoClose?: number | boolean;
  withCloseButton?: boolean;
}

function parseErrorMessage(err: unknown, fallback = 'An unexpected error occurred'): string {
  if (!err) return fallback;
  if (typeof err === 'string') return err;
  if (typeof err === 'object') {
    const errorAny = err as any;
    const msg = errorAny.response?.data?.message || errorAny.message;
    if (Array.isArray(msg)) return msg.filter(Boolean).join('\n'); // DTO validation: one error per line
    if (typeof msg === 'string' && msg.trim()) return msg;
  }
  return fallback;
}

const defaultStyles = {
  root: {
    backgroundColor: 'var(--surface)',
    border: '1px solid var(--boundary)',
    borderRadius: 8,
    boxShadow: 'var(--popover-shadow)',
  },
  title: {
    color: 'var(--text-primary)',
    fontWeight: 600,
    fontSize: 13,
  },
  description: {
    color: 'var(--text-secondary)',
    fontSize: 12,
    whiteSpace: 'pre-line' as const,
  },
};

export const toast = {
  success: (message: string, title = 'Success', options?: ToastOptions) => {
    notifications.show({
      id: options?.id,
      title,
      message,
      color: 'teal',
      icon: <CheckCircle size={18} weight="fill" />,
      autoClose: options?.autoClose ?? 3500,
      withCloseButton: options?.withCloseButton ?? true,
      styles: defaultStyles,
    });
  },

  error: (err: unknown, title = 'Error', options?: ToastOptions) => {
    const message = parseErrorMessage(err);
    notifications.show({
      id: options?.id,
      title,
      message,
      color: 'red',
      icon: <XCircle size={18} weight="fill" />,
      autoClose: options?.autoClose ?? 5000,
      withCloseButton: options?.withCloseButton ?? true,
      styles: defaultStyles,
    });
  },

  warning: (message: string, title = 'Warning', options?: ToastOptions) => {
    notifications.show({
      id: options?.id,
      title,
      message,
      color: 'yellow',
      icon: <WarningCircle size={18} weight="fill" />,
      autoClose: options?.autoClose ?? 4000,
      withCloseButton: options?.withCloseButton ?? true,
      styles: defaultStyles,
    });
  },

  info: (message: string, title = 'Information', options?: ToastOptions) => {
    notifications.show({
      id: options?.id,
      title,
      message,
      color: 'blue',
      icon: <Info size={18} weight="fill" />,
      autoClose: options?.autoClose ?? 3500,
      withCloseButton: options?.withCloseButton ?? true,
      styles: defaultStyles,
    });
  },
};

export default toast;
